/*
  Kiwi Ears · Velaris field (Rene 2026-08-23).

  A vanilla port of the "Velaris" React/shadcn component Rene supplied — the
  shader is carried over verbatim (simplex noise, four-colour blend, vignette,
  glow, film grain); the React wrapper is translated per CLAUDE.md, because
  this theme is Liquid + plain JS and adopting a JSX component is not an
  option. Palette re-pointed from the demo's greens to the brand violets in
  snippets/kiwi-herofield.liquid, where every hero without a photograph mounts
  a [data-velaris] canvas over the static violet radial.

  Departures from the component, all deliberate:

  · The static CSS field stays underneath. It is the no-JS, no-WebGL and
    context-lost fallback — the canvas fades in over it on the first rendered
    frame, so a hero is never a black rectangle while shaders compile.
  · prefers-reduced-motion renders ONE frame and stops. The gradient is a
    fine still image; it is the perpetual drift the setting exists to stop.
  · The loop pauses whenever the canvas leaves the viewport. Heroes sit at the
    top of long pages, and a rAF burning GPU behind three screens of scroll is
    pure waste. The component rendered forever.
  · DPR is clamped at 2, as the component clamps it — a hero-sized canvas at
    3x is a lot of pixels for a soft gradient nobody can resolve.
*/
(function () {
  if (window.__kiwiVelarisReady) return;
  window.__kiwiVelarisReady = true;

  var canvases = [].slice.call(document.querySelectorAll('canvas[data-velaris]'));
  if (!canvases.length) return;

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var VERT =
    'attribute vec2 position;varying vec2 vUv;void main(){vUv=position*0.5+0.5;gl_Position=vec4(position,0.0,1.0);}';

  var FRAG = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform vec2 u_resolution;',
    'uniform float u_time;',
    'uniform float u_grain;',
    'uniform vec3 u_colors[4];',
    'uniform vec3 u_bg;',
    'vec3 permute(vec3 x){return mod(((x*34.0)+1.0)*x,289.0);}',
    'float snoise(vec2 v){',
    ' const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);',
    ' vec2 i=floor(v+dot(v,C.yy));',
    ' vec2 x0=v-i+dot(i,C.xx);',
    ' vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);',
    ' vec4 x12=x0.xyxy+C.xxzz;',
    ' x12.xy-=i1;',
    ' i=mod(i,289.0);',
    ' vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));',
    ' vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);',
    ' m=m*m;m=m*m;',
    ' vec3 x=2.0*fract(p*C.www)-1.0;',
    ' vec3 h=abs(x)-0.5;',
    ' vec3 ox=floor(x+0.5);',
    ' vec3 a0=x-ox;',
    ' m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);',
    ' vec3 g;',
    ' g.x=a0.x*x0.x+h.x*x0.y;',
    ' g.yz=a0.yz*x12.xz+h.yz*x12.yw;',
    ' return 130.0*dot(m,g);}',
    'void main(){',
    ' vec2 uv=vUv;',
    ' float ratio=u_resolution.x/u_resolution.y;',
    ' vec2 p=uv-0.5;',
    ' p.x*=ratio;',
    ' float t=u_time*0.1;',
    ' float n1=snoise(p*0.4+vec2(t*0.2,-t*0.3));',
    ' float n2=snoise(p*0.55+vec2(-t*0.15,t*0.25)+n1*0.25);',
    ' float n3=snoise(p*0.75+vec2(t*0.1,-t*0.2)+n2*0.2);',
    ' vec3 col=u_bg;',
    ' float dist=length(p)*1.5;',
    ' float vignette=1.0-smoothstep(0.3,1.2,dist);',
    ' col=mix(col,u_colors[0],smoothstep(-0.2,0.5,n1)*0.85);',
    ' col=mix(col,u_colors[1],smoothstep(-0.1,0.6,n2)*0.7);',
    ' col=mix(col,u_colors[2],smoothstep(-0.3,0.4,n3)*0.6);',
    ' col=mix(col,u_colors[3],smoothstep(0.0,0.7,n1*n2)*0.5);',
    ' float glow=smoothstep(0.8,0.0,dist)*0.3;',
    ' col+=u_colors[1]*glow;',
    ' col=mix(col*0.2,col,vignette);',
    ' float grain=fract(sin(dot(uv,vec2(12.9898,78.233)))*43758.5453+u_time);',
    ' col+=(grain-0.5)*u_grain*0.1;',
    ' gl_FragColor=vec4(col,1.0);}'
  ].join('\n');

  function hexToRgb(hex) {
    var h = (hex || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return [
      parseInt(h.slice(0, 2), 16) / 255,
      parseInt(h.slice(2, 4), 16) / 255,
      parseInt(h.slice(4, 6), 16) / 255
    ];
  }

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { gl.deleteShader(s); return null; }
    return s;
  }

  function arm(canvas) {
    var host = canvas.parentElement;
    if (!host) return;

    var gl;
    try { gl = canvas.getContext('webgl', { antialias: false, depth: false }); }
    catch (e) { return; }
    if (!gl) return;

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    var pos = gl.getAttribLocation(prog, 'position');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    var locs = {
      res: gl.getUniformLocation(prog, 'u_resolution'),
      time: gl.getUniformLocation(prog, 'u_time'),
      grain: gl.getUniformLocation(prog, 'u_grain'),
      colors: gl.getUniformLocation(prog, 'u_colors'),
      bg: gl.getUniformLocation(prog, 'u_bg')
    };

    var speed = parseFloat(canvas.getAttribute('data-velaris-speed')) || 2.0;
    var grain = parseFloat(canvas.getAttribute('data-velaris-grain'));
    if (isNaN(grain)) grain = 0.3;
    var bg = hexToRgb(canvas.getAttribute('data-velaris-bg') || '#30246D');
    var colors = (canvas.getAttribute('data-velaris-colors') || '#9E81FB,#775ED9,#4F3AB6,#241C52')
      .split(',').slice(0, 4);
    while (colors.length < 4) colors.push(colors[colors.length - 1]);
    var flat = new Float32Array(12);
    colors.forEach(function (c, i) {
      var rgb = hexToRgb(c);
      flat[i * 3] = rgb[0]; flat[i * 3 + 1] = rgb[1]; flat[i * 3 + 2] = rgb[2];
    });

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.round(host.clientWidth * dpr), h = Math.round(host.clientHeight * dpr);
      if (!w || !h) return;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    }

    var shown = false;
    function frame(t) {
      resize();
      gl.uniform2f(locs.res, canvas.width, canvas.height);
      gl.uniform1f(locs.time, t * 0.001 * speed);
      gl.uniform1f(locs.grain, grain);
      gl.uniform3f(locs.bg, bg[0], bg[1], bg[2]);
      gl.uniform3fv(locs.colors, flat);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      if (!shown) {
        shown = true;
        /* fade in over the static field only once real pixels exist */
        canvas.classList.add('is-live');
      }
    }

    /* One frame under reduced motion — a still gradient, no drift. */
    if (still) { frame(0); return; }

    var raf = 0, running = false;
    function loop(t) {
      frame(t);
      raf = requestAnimationFrame(loop);
    }
    function start() { if (!running) { running = true; raf = requestAnimationFrame(loop); } }
    function stop() { if (running) { running = false; cancelAnimationFrame(raf); } }

    /* Heroes top long pages: no reason to shade pixels three screens away. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { en.isIntersecting ? start() : stop(); });
      }, { rootMargin: '80px' }).observe(canvas);
    } else {
      start();
    }

    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      stop();
      canvas.classList.remove('is-live');
    }, false);
  }

  function armAll() { canvases.forEach(arm); }
  if (document.readyState === 'complete') armAll();
  else window.addEventListener('load', armAll);
})();
